import * as React from "react"

import { cn } from "@/lib/utils"

const TableContext = React.createContext({ headers: [], setHeaders: () => {} });

const getTextFromNode = (node) => {
  if (!node) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getTextFromNode).join("");
  if (node.props && node.props.children) return getTextFromNode(node.props.children);
  return "";
};

const Table = React.forwardRef(({ className, ...props }, ref) => {
  const [headers, setHeaders] = React.useState([]);
  return (
    <TableContext.Provider value={{ headers, setHeaders }}>
      <div className="relative w-full overflow-auto">
        <table
          ref={ref}
          className={cn("w-full caption-bottom text-sm responsive-table", className)}
          {...props} />
      </div>
    </TableContext.Provider>
  );
})
Table.displayName = "Table"

const TableHeader = React.forwardRef(({ className, children, ...props }, ref) => {
  const { setHeaders } = React.useContext(TableContext);

  React.useEffect(() => {
    const headTexts = [];
    React.Children.forEach(children, (row) => {
      if (row && row.props && row.props.children) {
        React.Children.forEach(row.props.children, (cell) => {
          if (cell && cell.props) {
            headTexts.push(getTextFromNode(cell.props.children));
          }
        });
      }
    });
    if (headTexts.length > 0) {
      setHeaders(headTexts);
    }
  }, [children, setHeaders]);

  return (
    <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props}>
      {children}
    </thead>
  );
})
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props} />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
    {...props} />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef(({ className, children, ...props }, ref) => {
  const { headers } = React.useContext(TableContext);

  const mappedChildren = React.useMemo(() => {
    return React.Children.map(children, (child, index) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, {
          "data-label": headers[index] || "",
          index,
        });
      }
      return child;
    });
  }, [children, headers]);

  return (
    <tr
      ref={ref}
      className={cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className
      )}
      {...props}
    >
      {mappedChildren}
    </tr>
  );
})
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props} />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef(({ className, children, "data-label": dataLabel, index, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    data-label={dataLabel}
    {...props}
  >
    {dataLabel && (
      <span className="md:hidden font-semibold text-slate-500 mr-2 text-left block w-full max-w-[40%] truncate">
        {dataLabel}
      </span>
    )}
    <div className={dataLabel ? "md:contents flex justify-end text-right md:text-left w-full" : "w-full"}>
      {children}
    </div>
  </td>
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props} />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
